export const getGroupsQuery = /* GraphQL */ `
  query GetGroups {
    getGroups {
      companyId
      name
      zeroTrustId
      aggregate {
        totalDeviceCount
        onlineDeviceCount
      }
    }
  }
`;

export const avFieldsFragment = /* GraphQL */ `
  fragment AVFields on Device {
    av {
      rtpAvailable
      rtpStatus
      detectedAVSignature
      detectedAVName
      lastScanOn
      updatedOn
      definitionNumber
      avHealthStatus
      avInstalled
      lastReportOn
      scanStatus
      firewallStatus
      firewallAvailable
      scanAvailable
      updateDefinitionsAvailable
    }
  }
`;

export const groupFieldsFragment = /* GraphQL */ `
  fragment GroupFields on Device {
    group {
      companyId
      name
      zeroTrustId
      users {
        id
        userName
      }
      aggregate {
        totalDeviceCount
        onlineDeviceCount
      }
    }
  }
`;

const groupNameFieldFragment = /* GraphQL */ `
  fragment GroupNameFieldFragment on Device {
    group {
      name
    }
  }
`;

export const defaultFieldsFragment = /* GraphQL */ `
  fragment DefaultDeviceFields on Device {
    id
    status
    lastAccess
    lastLoggedInUser {
      userName
    }
    firstSeen
    lastSeen
    premium
    status
    zeroTrustEnabled
    deviceName
    labels
    hostName
    osName
    osVersion
    clientVersion
    clientType
    connectivity {
      lastChanged
      status
    }
    ipAddresses
    macAddresses
    biosVersion
    biosSerialNumber
    shellUrl
    companyId
    inSession
    locked
    platform
    processor
    systemFolder
    systemManufacturer
    systemModel
    timeZone
    timeZoneOffset
    tlsInfo
    totalPhysicalMemory
    userLocale
    windowsFolder
    verified
    deviceGroupId
    foreignSystemId
    foreignSystemKind
  }
`;

export const getDeviceByIdQuery = /* GraphQL */ `
  ${defaultFieldsFragment}
  ${groupFieldsFragment}
  ${avFieldsFragment}

  query getDevice($deviceId: ID!) {
    getDevice(input: { id: $deviceId }) {
      ...DefaultDeviceFields
      ...GroupFields
      ...AVFields
    }
  }
`;

export const getDevicesInGroupQuery = /* GraphQL */ `
  ${defaultFieldsFragment}
  ${avFieldsFragment}

  query getGroup($zeroTrustId: ID!, $offset: Int, $first: Int, $sort: Sort, $verified: Boolean) {
    getGroup(input: { zeroTrustId: $zeroTrustId }) {
      zeroTrustId
      aggregate(input: { verified: $verified }) {
        totalDeviceCount
      }
      devices(
        input: { pagination: { offset: $offset, first: $first, sort: $sort }, filters: { verified: $verified } }
      ) {
        ...DefaultDeviceFields
        ...AVFields
      }
    }
  }
`;

export const getInitialDevicesInGroupQuery = /* GraphQL */ `
  ${defaultFieldsFragment}
  ${avFieldsFragment}

  query getGroup($zeroTrustId: ID!, $offset: Int, $first: Int, $sort: Sort) {
    getGroup(input: { zeroTrustId: $zeroTrustId }) {
      zeroTrustId
      verifiedTotalCount: aggregate(input: { verified: true }) {
        totalDeviceCount
      }
      verifiedDevices: devices(
        input: { pagination: { first: $first, offset: $offset, sort: $sort }, filters: { verified: true } }
      ) {
        ...DefaultDeviceFields
        ...AVFields
      }
      unverifiedTotalCount: aggregate(input: { verified: false }) {
        totalDeviceCount
      }
      unverifiedDevices: devices(
        input: { pagination: { first: $first, offset: $offset, sort: $sort }, filters: { verified: false } }
      ) {
        ...DefaultDeviceFields
        ...AVFields
      }
    }
  }
`;

export const getSearchResult = /* GraphQL */ `
  ${defaultFieldsFragment}
  query GetSearchResult($searchText: String, $deviceGroupIds: [String!]) {
    getDevices(input: { advancedFilters: { searchText: $searchText, deviceGroupIds: $deviceGroupIds } }) {
      ...DefaultDeviceFields
    }
    getGroups(input: { searchText: $searchText }) {
      companyId
      name
      zeroTrustId
      aggregate {
        totalDeviceCount
        onlineDeviceCount
      }
    }
  }
`;

export const searchGroups = /* GraphQL */ `
  query SearchGroups($searchText: String) {
    getGroups(input: { searchText: $searchText }) {
      companyId
      name
      zeroTrustId
      aggregate {
        totalDeviceCount
        onlineDeviceCount
      }
    }
  }
`;

export const getFieldsQuery = withGroupName => /* GraphQL */ `
  ${defaultFieldsFragment}
  ${avFieldsFragment}
  ${withGroupName ? groupNameFieldFragment : ''}

  query GetDevicesQuery(
    $offset: Int
    $first: Int
    $sort: Sort
    $verified: Boolean
    $deviceGroupIds: [String!]
    $premium: Boolean
    $platforms: [Platform!]
    $statuses: [StatusFilter!]
    $labels: [String!]
    $lastAccess: DateFilter
    $lastSeen: DateFilter
    $av: AntiVirusFilters
    $searchText: String
  ) {
    getDevices(
      input: {
        pagination: { offset: $offset, first: $first, sort: $sort }
        advancedFilters: {
          verified: $verified
          deviceGroupIds: $deviceGroupIds
          premium: $premium
          platforms: $platforms
          statuses: $statuses
          labels: $labels
          lastAccess: $lastAccess
          lastSeen: $lastSeen
          av: $av
          searchText: $searchText
        }
      }
    ) {
      ...DefaultDeviceFields
      ...AVFields
      ${withGroupName ? `...GroupNameFieldFragment` : ''}
    }
    getAggregateBy(
      input: {
        advancedFilters: {
          verified: $verified
          deviceGroupIds: $deviceGroupIds
          premium: $premium
          platforms: $platforms
          statuses: $statuses
          labels: $labels
          lastAccess: $lastAccess
          av: $av
          searchText: $searchText
        }
      }
    ) {
      aggregate {
        totalDeviceCount
      }
    }
  }
`;

export const getDevicesInitCounters = /* GraphQL */ `
  query GetDevicesInitCounters {
    getAggregateBy(input: {}) {
      aggregate {
        proDeviceCount
        liteDeviceCount
      }
    }
  }
`;

export const getAllDevicesCounter = /* GraphQL */ `
  query GetAllDeviceCounter($verified: Boolean) {
    getAggregateBy(input: { advancedFilters: { verified: $verified } }) {
      aggregate {
        totalDeviceCount
      }
    }
  }
`;

export const getAllDeviceLabels = /* GraphQL */ `
  query GetAllDeviceLabels {
    getAggregateBy {
      aggregate {
        labels
      }
    }
  }
`;

const defaultFieldsFragmentWithGroupName = /* GraphQL */ `
  fragment DefaultDeviceFields on Device {
    id
    status
    lastAccess
    lastLoggedInUser {
      userName
    }
    group {
      name
    }
    firstSeen
    lastSeen
    premium
    status
    zeroTrustEnabled
    deviceName
    labels
    hostName
    osName
    osVersion
    clientVersion
    clientType
    ipAddresses
    macAddresses
    biosVersion
    biosSerialNumber
    shellUrl
    companyId
    inSession
    locked
    platform
    processor
    systemFolder
    systemManufacturer
    systemModel
    timeZone
    timeZoneOffset
    tlsInfo
    totalPhysicalMemory
    userLocale
    windowsFolder
    verified
    deviceGroupId
    foreignSystemId
    foreignSystemKind
  }
`;

export const getDevicesWithoutAggregate = /* GraphQL */ `
  ${defaultFieldsFragmentWithGroupName}
  ${avFieldsFragment}

  query GetDevicesQuery(
    $offset: Int
    $first: Int
    $sort: Sort
    $verified: Boolean
    $deviceGroupId: String
    $premium: Boolean
    $platform: Platform
    $status: Status
    $av: AntiVirusFilters
  ) {
    getDevices(
      input: {
        pagination: { offset: $offset, first: $first, sort: $sort }
        filters: {
          verified: $verified
          deviceGroupId: $deviceGroupId
          premium: $premium
          platform: $platform
          status: $status
          av: $av
        }
      }
    ) {
      ...DefaultDeviceFields
      ...AVFields
    }
  }
`;

export const getDeviceLicenseCountQuery = /* GraphQL */ `
  query getDeviceLicenseCount {
    me {
      company {
        deviceLicenseCount
      }
    }
  }
`;

export const UpdateDevicesQuery = /* GraphQL */ `
  mutation UpdateDevics($input: UpdateDeviceInput!) {
    updateDevice(input: $input) {
      id
      deviceName
      labels
    }
  }
`;
